import React, { useEffect, useRef } from 'react';
import { Animated, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { MaterialCommunityIcons as Icon } from '@expo/vector-icons';
import { router } from 'expo-router';
import { REDESIGN_COLORS } from '../../styles/colors';
import { useTier } from '../../context/TierContext';

// D-027 copy matrix. Branch keys are derived in TierContext from prev_status
// (see resolveProActivationBranch). Body is built from a per-branch composer
// so we can conditionally include saved-listing / active-model clauses only
// when counts are ≥1 (prod median is 0 or 1, see flow_decisions.md).
const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const pluralize = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

const buildBody = (branch, { savedCount = 0, modelCount = 0, proExpiresAt = null } = {}) => {
  const savedClause = savedCount > 0
    ? ` Your ${pluralize(savedCount, 'saved listing', 'saved listings')}`
    : '';
  const modelClause = modelCount > 0
    ? (savedClause ? ` and ${pluralize(modelCount, 'active model', 'active models')}` : ` Your ${pluralize(modelCount, 'active model', 'active models')}`)
    : '';
  const tail = (savedClause || modelClause) ? ' are right where you left them.' : '';

  switch (branch) {
    case 'trial_convert':
      return `Full scores, running costs, and AI negotiation are yours.${savedClause}${modelClause}${tail}`;
    case 'grandfather_convert':
      return 'You are on the Founder\u2019s Rate. Your models are fully active.';
    case 'lapsed_convert': {
      const modelRestored = modelCount > 0
        ? `Your ${pluralize(modelCount, 'model is', 'models are')} active again and we\u2019re scanning for matches now.`
        : 'Your models are active again and we\u2019re scanning for matches now.';
      const savedRestored = savedCount > 0
        ? ` Your ${pluralize(savedCount, 'saved listing is', 'saved listings are')} restored.`
        : '';
      return `${modelRestored}${savedRestored}`;
    }
    case 'first_time':
      return 'Start with your feed. Tap any listing to see your full score breakdown, running costs forecast, and AI negotiation.';
    case 'cbp':
      return `Full access is live. Score breakdowns, running costs, and AI negotiation are ready. Your pass runs through ${formatDate(proExpiresAt) || '90 days from today'}.`;
    case 'trial_carded_unlock':
      // Step 8: the user added a card and unlocked full Pro before any charge.
      return 'Your card is on file and full Pro is unlocked. You are not charged until your trial ends.';
    default:
      return '';
  }
};

const BRANCH_META = {
  trial_convert: { headline: 'You\u2019re in.', cta: { label: 'Start negotiating', href: '/home' } },
  grandfather_convert: { headline: 'Founder\u2019s Rate locked in.', cta: { label: 'See what\u2019s new', href: '/home' } },
  lapsed_convert: { headline: 'Welcome back.', cta: { label: 'See your matches', href: '/home' } },
  first_time: { headline: 'You\u2019re in.', cta: { label: 'See your matches', href: '/home' } },
  cbp: { headline: '90 days to find your car.', cta: { label: 'Start negotiating', href: '/home' } },
  // Step 8: card-add reveal. Pro is unlocked, first charge deferred to day 7.
  trial_carded_unlock: { headline: 'Pro unlocked.', cta: { label: 'See your matches', href: '/home' } },
};

const AUTO_DISMISS_MS = 10000;

const ProActivationToast = () => {
  const { proActivationToast, dismissProActivationToast, proExpiresAt } = useTier();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  // Mobile slides down from top (-40); desktop slides up from bottom (+40).
  const slideAnim = useRef(new Animated.Value(isMobile ? -40 : 40)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const dismissRef = useRef(dismissProActivationToast);
  useEffect(() => { dismissRef.current = dismissProActivationToast; }, [dismissProActivationToast]);

  useEffect(() => {
    if (!proActivationToast) return;
    Animated.parallel([
      Animated.timing(slideAnim, { toValue: 0, duration: 320, useNativeDriver: true }),
      Animated.timing(fadeAnim, { toValue: 1, duration: 320, useNativeDriver: true }),
    ]).start();
    const timer = setTimeout(() => dismissRef.current?.(), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [proActivationToast, slideAnim, fadeAnim]);

  if (!proActivationToast) return null;
  const meta = BRANCH_META[proActivationToast.branch];
  if (!meta) return null;
  const body = buildBody(proActivationToast.branch, {
    savedCount: proActivationToast.savedCount,
    modelCount: proActivationToast.modelCount,
    proExpiresAt,
  });

  const handleCta = () => {
    dismissProActivationToast();
    if (meta.cta?.href) router.push(meta.cta.href);
  };

  return (
    <Animated.View
      pointerEvents="box-none"
      style={[
        styles.wrapper,
        isMobile ? styles.wrapperMobile : styles.wrapperDesktop,
        { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
      ]}
    >
      <View style={styles.card}>
        <View style={styles.iconWrap}>
          <Icon name="crown" size={20} color={REDESIGN_COLORS.ctaBlue} />
        </View>
        <View style={styles.content}>
          <Text style={styles.headline}>{meta.headline}</Text>
          <Text style={styles.body}>{body}</Text>
          {meta.cta && (
            <Pressable onPress={handleCta} style={styles.ctaBtn} accessibilityRole="button" accessibilityLabel={meta.cta.label}>
              <Text style={styles.ctaText}>{meta.cta.label}</Text>
              <Icon name="arrow-right" size={14} color={REDESIGN_COLORS.textPrimary} />
            </Pressable>
          )}
          <Text style={styles.tagline}>Happy car hunting!</Text>
        </View>
        <Pressable onPress={dismissProActivationToast} hitSlop={8} accessibilityRole="button" accessibilityLabel="Dismiss" style={styles.closeBtn}>
          <Icon name="close" size={16} color={REDESIGN_COLORS.textSecondary} />
        </Pressable>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    zIndex: 9999,
  },
  wrapperMobile: {
    top: 12,
    left: 12,
    right: 12,
  },
  wrapperDesktop: {
    bottom: 20,
    right: 20,
    width: 360,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(83,138,249,0.35)',
    backgroundColor: '#0E264D',
    ...(Platform.OS === 'web' && {
      backgroundImage:
        'radial-gradient(240px 120px at 100% 0%, rgba(83,138,249,0.28), transparent 60%), linear-gradient(180deg, rgba(20,76,184,0.35) 0%, rgba(14,38,77,0.9) 100%)',
      boxShadow: '0 12px 28px rgba(5,86,247,0.25)',
    }),
  },
  iconWrap: {
    width: 32, height: 32, borderRadius: 16,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(5,86,247,0.18)',
    borderWidth: 1, borderColor: 'rgba(83,138,249,0.4)',
  },
  content: { flex: 1 },
  headline: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
    marginBottom: 4,
  },
  body: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    lineHeight: 17,
    color: 'rgba(255,255,255,0.75)',
  },
  ctaBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    marginTop: 10,
  },
  ctaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    color: REDESIGN_COLORS.textPrimary,
  },
  closeBtn: {
    padding: 2,
  },
  tagline: {
    marginTop: 10,
    fontFamily: 'Satoshi-Medium',
    fontSize: 11,
    color: 'rgba(255,255,255,0.55)',
  },
});

export default ProActivationToast;
